import { RootState } from '../../redux/store';
import { CreateIncidentRequest, UpdateIncidentRequest, PhotoPayload } from '../../types/api.types';
import { DisplayPhoto, PhotoTypes, scrubBlobUrl } from '../../redux/reducers/vehiclePhotosSlice';

// Maps Redux damaged part keys to exact C# DamageLocationPartViewModel property names.
// Keys that share a name across zones (e.g. "Windshield" in Front and Rear) map to the
// same C# property — if either is selected the boolean should be true.
const PART_TO_PROPERTY: Record<string, Record<string, string>> = {
  front: {
    'Bumper': 'FrontBumper',
    'Grille': 'Grille',
    'Hood': 'Hood',
    'Windshield': 'Windshield',
    'Fender': 'FrontFender',
    'Headlamp': 'HeadLamp',
    'Corner Lamp': 'CornerLamp',
  },
  rear: {
    'Bumper': 'RearBumper',
    'Rear Body Panel': 'RearBodyPanel',
    'Trunk Lid/Tailgate': 'TrunkLidTailgate',
    'Windshield': 'Windshield',
    'Fender (Quarter Panel)': 'FenderQuarterPanel',
    'Tail Lamp': 'TailLamp',
    'Side Marker Lamp': 'SideMarkerLamp',
  },
  side: {
    'Door(s)': 'Doors',
    'Side Mirror': 'SideMirror',
    'Door Glass': 'DoorGlass',
  },
};

function transformDamagedParts(
  front: Record<string, boolean>,
  rear: Record<string, boolean>,
  side: Record<string, boolean>,
): Record<string, boolean> {
  const result: Record<string, boolean> = {};
  const zones: Array<[Record<string, boolean>, Record<string, string>]> = [
    [front, PART_TO_PROPERTY.front],
    [rear, PART_TO_PROPERTY.rear],
    [side, PART_TO_PROPERTY.side],
  ];
  for (const [parts, mapping] of zones) {
    for (const [partKey, isSelected] of Object.entries(parts)) {
      if (isSelected && mapping[partKey]) {
        result[mapping[partKey]] = true;
      }
    }
  }
  return result;
}

export const transformIncidentData = (
  state: RootState,
  isUpdate: boolean = false
): CreateIncidentRequest | UpdateIncidentRequest => {
  const {
    vehicle,
    operatorInfo,
    damageLocations,
    damagedParts,
    incidentDetails,
  } = state;

  // Transform damage locations to API format
  const damageLocationsObj = Object.fromEntries(
    Object.entries(damageLocations.damageLocation).filter(([, v]) => v)
  );

  // Transform damaged parts to flat boolean object matching C# property names
  const damagedPartsObj = transformDamagedParts(
    damagedParts.frontDamagedParts,
    damagedParts.rearDamagedParts,
    damagedParts.sideDamagedParts,
  );

  // Base incident data
  const baseData: CreateIncidentRequest = {
    IncidentDate: incidentDetails.incidentDate.toISOString(),
    OperatorInjured: operatorInfo.operatorInjured || false,
    OperatorId: operatorInfo.selectedOperator?.operatorId,
    VehicleId: vehicle.vehicle?.vehicleId,
    Vin: vehicle.vehicle?.vin || '',
    VehicleNumber: vehicle.vehicle?.vehicleNumber || '',
    DamagedLocations: damageLocationsObj,
    DamagedParts: damagedPartsObj,
    IncidentId: incidentDetails.incidentId || 0,
    Finalize: false,
    VehicleAdditionalInfo: vehicle.additionalInformation || undefined,
    VehicleIsDrivable: vehicle.vehicleIsDrivable,
    Mileage: vehicle.currentMileage,
    PoliceCaseNumber: incidentDetails.policeCaseNumber,
    AccidentLocationDescription: incidentDetails.accidentLocationDescription,
    AdditionalSafetyNotes: incidentDetails.additionalSafetyNotes,
    InjuryComplaints: incidentDetails.injuryComplaints,
    IncidentType: incidentDetails.incidentType || undefined,
    OtherParties: incidentDetails.otherParties || undefined,
    OtherProperty: incidentDetails.otherProperty || undefined,
    OtherVehicles: incidentDetails.otherVehicles || undefined,
    // Only send the Azure URL and status — never include the local data URI (base64)
    // which would make the request body massive. This matches the mobile app behavior.
    Photos: (Object.values(state.vehiclePhotos.photoMap).filter(Boolean) as DisplayPhoto[]).map((photo): PhotoPayload => ({
      url: scrubBlobUrl(photo.url),
      type: photo.type,
      uploadStatus: photo.uploadStatus
    }))
  };

  if (isUpdate) {
    return {
      ...baseData,
      IncidentId: incidentDetails.incidentId
    } as UpdateIncidentRequest;
  }

  return baseData;
};

export const formatMileage = (mileage: number): string => {
  return mileage.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const parseMileage = (mileageStr: string): number => {
  return parseInt(mileageStr.replace(/,/g, ''), 10) || 0;
};

export const formatDateTime = (date: Date): string => {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

export const formatDate = (date: Date): string => {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }).format(date);
};

export const getPhotoTypeLabel = (type: PhotoTypes): string => {
  return PhotoTypes[type];
};

export const base64ToBlob = (base64: string, contentType: string = 'image/jpeg'): Blob => {
  const byteCharacters = atob(base64.split(',')[1]);
  const byteNumbers = new Array(byteCharacters.length);
  
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: contentType });
};

export const resizeImage = (
  base64: string,
  maxWidth: number,
  maxHeight: number,
  quality: number = 0.8
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let { width, height } = img;
      
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width *= ratio;
        height *= ratio;
      }
      
      canvas.width = width;
      canvas.height = height;
      
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }
      
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    
    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };
    
    img.src = base64;
  });
};